
import React from 'react';
import { VideoFeatures, ActionButton } from '@/store/videoFeatures';
import { MiniHookPosition } from '@/store/videoFeatures/types/mini-hook';
import { LogoOverlay } from './ui/LogoOverlay';
import { HeadlineDisplay } from './ui/HeadlineDisplay';
import { ActionButtonOverlay } from './ui/ActionButtonOverlay';
import { ViewerCount } from './ui/ViewerCount';
import { BigPlayButton } from './ui/BigPlayButton';
import { SalesNotificationOverlay } from './ui/SalesNotificationOverlay';
import { AffiliateLogo } from './ui/AffiliateLogo';


interface VideoOverlaysProps {
  features: VideoFeatures;
  currentTime: number;
  shouldShowHeadline: boolean;
  /** Botões "sobre o vídeo" a desenhar agora (já filtrados por ligado/tempo). */
  overActionButtons: ActionButton[];
  isPlaying: boolean;
  autoplayOverlayEnabled: boolean;
  showAutoplayOverlay: boolean;
  togglePlay: () => void;
  onSkipBack?: () => void;
  onSkipForward?: () => void;
  isVideoEnded?: boolean;
  // Painel aberto no editor. Só a Notificação de Vendas usa: ela é
  // "preview-first" (monta mesmo desligada) e sem esse gate apareceria forçada
  // enquanto o usuário edita qualquer OUTRO recurso, já que `isPreview` é
  // derivado apenas do pathname.
  editingPanel?: string | null;
}

export const VideoFeatureOverlays: React.FC<VideoOverlaysProps> = ({
  features,
  currentTime,
  shouldShowHeadline,
  overActionButtons,
  isPlaying,
  autoplayOverlayEnabled,
  showAutoplayOverlay,
  togglePlay,
  onSkipBack,
  onSkipForward,
  isVideoEnded = false,
  editingPanel = null
}) => {
  // Added null check for features
  if (!features) {
    console.warn('VideoFeatureOverlays: features prop is undefined or null');
    return null;
  }

  // Detect preview mode for sales notification
  const isPreview = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/new-video') || 
    window.location.pathname.startsWith('/personalizations')
  );

  // Gate do preview forçado da Notificação de Vendas: só no editor E só com o
  // painel dela aberto.
  const salesNotificationPreview = isPreview && editingPanel === 'salesNotification';

  // Determine if we should show the big play button
  const shouldShowPlayButton = features.bigPlayButton &&
    !isPlaying &&
    !(autoplayOverlayEnabled && showAutoplayOverlay) &&
    !features.disableControls;

  // Tema dos controles (afeta o BigPlayButton e os demais ícones). 'custom' é
  // o id legado do tema Safari (upload de ícone descontinuado) — normalizado
  // pra 'safari' pra manter compat com vídeos já salvos.
  const rawControlsTheme = features.controlsTheme === 'custom' ? 'safari' : features.controlsTheme;
  const controlsTheme = rawControlsTheme &&
    ['minimal', 'solid', 'duotone', 'bold', 'safari'].includes(rawControlsTheme) ?
    rawControlsTheme as 'minimal' | 'solid' | 'duotone' | 'bold' | 'safari' :
    'minimal';

  // Detect if autoplay small (pequeno) template is active
  // Verifica se o template small está configurado e o autoplay está habilitado
  const isAutoplaySmallActive =
    features.autoplayOverlay?.enabled &&
    features.autoplayOverlay?.templateId === 'small';

  // Set up headline testing - removed old system
  // const {
  //   currentVariation,
  //   testActive,
  //   handlePlay,
  //   handleButtonClick
  // } = useHeadlineTest(features.headline);
  
  // Register play event - removed old headline test
  // React.useEffect(() => {
  //   if (isPlaying) {
  //     handlePlay();
  //   }
  // }, [isPlaying, handlePlay]);

  return (
    <>
      {/* Logo - Added null check */}
      {features.logo && features.logo.enabled && (
        <LogoOverlay logo={features.logo} />
      )}

      {/* OCULTO TEMPORARIAMENTE - Será implementado futuramente */}
      {/* Affiliate Logo - HostVSL */}
      {/* {features.affiliateTracking &&
       features.affiliateTracking.enabled &&
       features.affiliateTracking.affiliateUrl && (
        <AffiliateLogo
          affiliate={features.affiliateTracking}
          currentTime={currentTime}
          isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
        />
      )} */}

      {/* Headline - New unified system */}
      {features.headlineVisible && shouldShowHeadline && features.headline && (
        <HeadlineDisplay
          headline={features.headline}
          currentTime={currentTime}
        />
      )}

      {/* Botões de ação "sobre o vídeo" — um por botão */}
      {overActionButtons.map((button) => (
        <ActionButtonOverlay
          key={button.id}
          actionButton={button}
          controlsColor={features.playerColors?.primary || features.controlsColor || '#FFFFFF'}
          onClick={() => {}}
          isVideoEnded={isVideoEnded}
        />
      ))}

      {/* Audience Counter - Added null check */}
      {features.audience && features.audience.enabled && (
        <ViewerCount
          audience={features.audience}
          forceShow={isPreview}
          autoplaySmallActive={isAutoplaySmallActive}
        />
      )}

      {/* Notificação de Vendas — preview-first: no editor monta mesmo quando o
          recurso está desligado, mas SÓ enquanto o painel dela está aberto. */}
      {(isPlaying || salesNotificationPreview) &&
        features.salesNotification &&
        (features.salesNotification.enabled || salesNotificationPreview) && (
        <SalesNotificationOverlay
          salesNotification={features.salesNotification}
          forceShow={salesNotificationPreview}
        />
      )}


      {/* Big Play Button - Shows when video is paused
          - Always show when video is paused (not playing)
          - Hide when autoplay overlay is visible
          - Hide when controls are disabled
      */}
      {/* Mini Hooks */}
      {(() => {
        const mh = features.miniHooks;
        if (!mh?.enabled || !Array.isArray(mh.hooks) || mh.hooks.length === 0) return null;

        const positionStyle: Record<MiniHookPosition, React.CSSProperties> = {
          'top-left':      { top: '5%', left: '5%' },
          'top-center':    { top: '0%', left: '50%', transform: 'translateX(-50%)' },
          'top-right':     { top: '5%', right: '5%' },
          'middle-left':   { top: '50%', left: '5%', transform: 'translateY(-50%)' },
          'center':        { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
          'middle-right':  { top: '50%', right: '5%', transform: 'translateY(-50%)' },
          'bottom-left':   { bottom: '5%', left: '5%' },
          'bottom-center': { bottom: '0%', left: '50%', transform: 'translateX(-50%)' },
          'bottom-right':  { bottom: '5%', right: '5%' },
        };

        const visibleHooks = mh.hooks.filter(
          (h) =>
            h?.isActive &&
            h.fileUrl &&
            currentTime >= h.startTimeSeconds &&
            currentTime <= h.endTimeSeconds
        );
        if (visibleHooks.length === 0) return null;

        return (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 20 }}>
            {visibleHooks.map((hook) => (
              <img
                key={hook.id}
                src={hook.fileUrl}
                alt=""
                style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  width: `${hook.widthPercent ?? 25}%`,
                  height: 'auto',
                  opacity: (hook.opacity ?? 100) / 100,
                  ...positionStyle[hook.position ?? 'bottom-right'],
                }}
              />
            ))}
          </div>
        );
      })()}

      {shouldShowPlayButton && (
        <BigPlayButton
          onClick={togglePlay}
          iconType={controlsTheme}
          color={features.controlColorIcon || features.playerColors?.primary || features.controlsColor || '#FFFFFF'}
          circleColor={features.controlColor || features.playerColors?.primary || '#FFFFFF'}
          onSkipBack={features.rewind ? onSkipBack : undefined}
          onSkipForward={features.skipForward ? onSkipForward : undefined}
        />
      )}
    </>
  );
};
